import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { DEFAULT_SORT_FOR_SEARCH } from "../../../../constants/defaultSort";
import { FilterValues } from "../../../../data-model/filterValues";
import { MedicalPaOrdersAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { isExceptionFlowForUrologyOrg } from "../../../../enums/organizationTypes";
import {
  processFilterData,
  transformDateFields,
  transformObjectToFacetFiltersForPostgres,
} from "../../../../utils/queryConstraint";

const TAB_IDS_WITH_DEFAULT_SORT: ReadonlySet<string> = new Set([
  MedicalPaOrdersAuthStatus.AuthRequired,
  MedicalPaOrdersAuthStatus.Pending,
  MedicalPaOrdersAuthStatus.Hold,
  MedicalPaOrdersAuthStatus.Query,
]);

/**
 * Auth-status tabIds whose worklists are shared across users for urology
 * exception-flow orgs (i.e. must not be scoped by `assigned_to`). Stored in
 * the internal underscored form because `genericAuthStatusOrders.tsx` swaps
 * the URL slug's hyphens to underscores via `swapUnderscoreHyphen` before
 * passing tabId into this function.
 *
 * URL slugs:
 * - `not-to-work-appointments` → `not_to_work_appointments`
 * - `orders-to-process` → `orders_to_process`
 */
const SUNSTATE_UROLOGY_SHARED_TAB_IDS: ReadonlySet<string> = new Set([
  "not_to_work_appointments",
  "orders_to_process",
]);

/**
 * Returns true when the current user's `assigned_to` filter should be omitted
 * from `facet_filters`. Certain exception-flow org worklists (see
 * {@link SUNSTATE_UROLOGY_SHARED_TAB_IDS}) are shared across users, so the
 * worklist must not be scoped to the currently logged-in user's assignments.
 *
 * @param tabId - Current auth status tab identifier (underscored form)
 * @param orgId - Organization ID for scoping the query
 * @returns True when `assigned_to` should be excluded from facet_filters
 */
const shouldExcludeAssignedToFilter = (tabId: string, orgId: string): boolean =>
  isExceptionFlowForUrologyOrg(orgId) &&
  SUNSTATE_UROLOGY_SHARED_TAB_IDS.has(tabId);

/**
 * Builds a Postgres search payload from active filters, search term, and user context
 * for a specific auth status tab. Merges the tab's master_auth_status and the current
 * user's assigned_to into the base filter set. When a sort field is provided, it is
 * included as `sort_by` / `sort_dir` so the API returns rows in the requested order.
 *
 * `assigned_to` is intentionally omitted from `facet_filters` for
 * exception-flow org shared worklists (see {@link shouldExcludeAssignedToFilter}
 * and {@link SUNSTATE_UROLOGY_SHARED_TAB_IDS}), so those worklists return rows
 * for every assignee in that org instead of being scoped to the currently
 * logged-in user.
 *
 * @param validFilters - Currently applied filter values from the filter panel
 * @param searchTerm - Free-text search term entered by the user
 * @param orgId - Organization ID for scoping the query
 * @param userId - Current user ID to filter by assigned_to (skipped for
 *   exception-flow shared worklists listed in
 *   {@link SUNSTATE_UROLOGY_SHARED_TAB_IDS})
 * @param facilityId - Health facility ID for the current session
 * @param isAdmin - Whether the current user has admin privileges
 * @param tabId - Auth status tab identifier used as master_auth_status filter value.
 *   When the tab is `auth_required`, `pending`, `hold`, or `query`, the request also
 *   includes `default_sort` (DEFAULT_SORT_FOR_SEARCH) as a server-side tiebreaker;
 *   other tabs omit it.
 * @param sortByField - Optional canonical API field name to sort by
 * @param sortAscending - Optional sort direction; defaults to ascending when sortByField is set
 * @returns Fully constructed search parameters for the Postgres PA orders query
 */
export const generateFilterPayloadForGenericAuthStatus = (
  validFilters: FilterValues[],
  searchTerm: string,
  orgId: string,
  userId: string,
  facilityId: string,
  isAdmin: boolean,
  tabId: string,
  sortByField?: string,
  sortAscending?: boolean,
): SearchForMedicalPaOrdersFromPostgresParams => {
  const filterData = processFilterData(validFilters);

  const baseFilters: Record<string, string[]> = {
    ...filterData,
    master_auth_status: [tabId],
    ...(shouldExcludeAssignedToFilter(tabId, orgId)
      ? {}
      : { assigned_to: [userId] }),
  };

  const createdFilterQuery =
    transformObjectToFacetFiltersForPostgres(baseFilters);
  const dateFilterQuery = transformDateFields(baseFilters);

  return {
    org_id: orgId || "",
    query: searchTerm ?? "",
    facet_filters: createdFilterQuery,
    hits_per_page: 100,
    page: 0,
    ...dateFilterQuery,
    timezone_offset: new Date().getTimezoneOffset(),
    ...(sortByField
      ? {
          sort_by: sortByField,
          sort_dir: sortAscending === false ? "desc" : "asc",
        }
      : {}),
    ...(TAB_IDS_WITH_DEFAULT_SORT.has(tabId)
      ? { default_sort: DEFAULT_SORT_FOR_SEARCH }
      : {}),
  };
};
